import Binder, { Binding, ClassBinding } from "./binder";
import ClassMap from "./classMap";

export default class Injector {
  public binders: Binder[];
  private singletons: ClassMap;
  private stack: string[];

  constructor(...binders: Binder[]) {
    this.binders = binders.reverse();
    this.singletons = new ClassMap();
    this.stack = [];
  }

  getInstance<Constructor extends new (...args: any[]) => unknown>(
    injectable: Constructor,
    ...args: unknown[]
  ): InstanceType<Constructor>;
  getInstance<Instance>(injectable: unknown, ...args: unknown[]): Instance;
  getInstance(cls: unknown, ...args: unknown[]) {
    const instanceName = (cls as Function).name ?? `'${cls}'`;

    this.stack.push(instanceName);

    try {
      for (let binder of this.binders) {
        for (let binding of binder.bindings) {
          const instance = this.resolve(cls, binding);

          if (instance != null) {
            return instance;
          }
        }
      }

      if ((cls as Function).prototype == null) {
        throw Error(`Could not resolve: ${this.stack.join(" -> ")}`);
      }

      return this.createNew(cls as new () => unknown, undefined, args);
    } finally {
      this.stack.pop();
    }
  }

  private createNew<
    Class extends {
      new (...args: any[]): unknown;
      scope?: string;
      _injectArguments_?: {
        parameterIndex: number;
        cls: { new (): unknown };
      }[];
    },
  >(cls: Class, scope?: string, args: unknown[] = []) {
    if (cls === Injector) {
      return this;
    }

    const singleton = this.singletons.get(cls);

    if (singleton != null) {
      return singleton;
    }

    const isClass = !Object.getOwnPropertyDescriptor(cls, "prototype")
      ?.writable;

    let instance: unknown;

    if (isClass) {
      const args = (cls._injectArguments_ ?? []).reduce<unknown[]>(
        (args, { parameterIndex, cls }) => {
          const instance = this.getInstance(cls);
          args[parameterIndex] = instance;
          return args;
        },
        [],
      );
      instance = new cls(...args);
    } else {
      const ptype = function () {} as unknown as { new (): {} };
      ptype.prototype = cls.prototype;
      instance = new ptype();
      this.injectFields(cls.prototype, instance as InstanceType<typeof ptype>);
      cls.apply(instance, args);
    }

    if (
      scope?.toUpperCase?.() === "SINGLETON" ||
      cls.scope?.toUpperCase?.() === "SINGLETON"
    ) {
      this.singletons.set(cls, instance);
    }

    return instance;
  }

  private resolve(cls: unknown, binding: Binding) {
    if (binding.matches(cls)) {
      const defaultFactory = () => {
        const { impl, scope } = binding as ClassBinding;
        return this.createNew(impl!, scope);
      };

      return binding.create(defaultFactory);
    }
    return undefined;
  }

  private injectFields(
    ptype: Record<
      string,
      {
        _requiresInjection_?: boolean;
        cls: new () => unknown;
        args: unknown[];
      }
    >,
    instance: Record<string, unknown>,
  ) {
    for (let k in ptype) {
      const v = ptype[k];

      if (v?._requiresInjection_) {
        instance[k] = this.getInstance(v.cls, ...v.args);
      }
    }
  }
}
